import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Phone, WhatsappLogo, EnvelopeSimple, MapPin, CircleNotch, ArrowUpRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT = [
    { icon: Phone, label: "Call us", value: "+91 97112 20094", href: "tel:+919711220094", testid: "contact-phone" },
    { icon: WhatsappLogo, label: "WhatsApp", value: "+91 97112 20094", href: "https://wa.me/919711220094", testid: "contact-whatsapp" },
    { icon: EnvelopeSimple, label: "Email", value: "akashcargomovers@gmail.com", href: "mailto:akashcargomovers@gmail.com", testid: "contact-email" },
    { icon: MapPin, label: "Head office", value: "F26-B, Gali No.2, Molarband Extension, Badarpur, New Delhi-110044", href: null, testid: "contact-address" },
];

const SERVICES = ["20 FT Container", "40 FT Container", "Heavy Machinery", "Freight Forwarding", "Other"];

export default function Enquiry() {
    const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
    const [loading, setLoading] = useState(false);

    const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.service) {
            toast.error("Please select a service.");
            return;
        }
        setLoading(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("Enquiry received. Our team will call you back shortly.");
            setForm({ name: "", phone: "", email: "", service: "", message: "" });
        } catch (err) {
            toast.error("Could not send your enquiry. Please call us directly.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="enquiry" className="border-t border-white/10" data-testid="enquiry-section">
            <div className="grid lg:grid-cols-2">
                <div className="flex flex-col justify-between border-b border-white/10 px-5 py-20 md:px-10 md:py-28 lg:border-b-0 lg:border-r">
                    <motion.div
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <p className="mb-4 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-blaze md:text-xs">
                            <span className="inline-block h-2 w-2 bg-blaze" />
                            Get a Quote
                        </p>
                        <h2 className="font-display text-5xl uppercase leading-[0.92] tracking-tight md:text-7xl">
                            Let's <br />
                            <span className="text-outline">move it.</span>
                        </h2>
                        <p className="mt-8 max-w-sm text-sm leading-relaxed text-white/55 md:text-base">
                            Tell us what you're moving and where it's headed. A move specialist from the right group company calls you back.
                        </p>
                    </motion.div>

                    <motion.ul
                        initial={{ opacity: 0, y: 32 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-16 space-y-6"
                    >
                        {CONTACT.map((item) => {
                            const Icon = item.icon;
                            const inner = (
                                <>
                                    <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-white/15 text-blaze transition-colors duration-200 group-hover:border-blaze">
                                        <Icon size={20} weight="duotone" />
                                    </span>
                                    <span>
                                        <span className="block text-[10px] uppercase tracking-[0.25em] text-white/40">{item.label}</span>
                                        <span className="mt-1 block text-base font-medium text-white md:text-lg">{item.value}</span>
                                    </span>
                                </>
                            );
                            return (
                                <li key={item.label}>
                                    {item.href ? (
                                        <a href={item.href} target={item.href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="group flex items-center gap-5" data-testid={item.testid}>
                                            {inner}
                                        </a>
                                    ) : (
                                        <div className="group flex items-center gap-5" data-testid={item.testid}>{inner}</div>
                                    )}
                                </li>
                            );
                        })}
                    </motion.ul>
                </div>

                <motion.form
                    onSubmit={handleSubmit}
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                    className="px-5 py-20 md:px-10 md:py-28 lg:px-16"
                    data-testid="enquiry-form"
                >
                    <div className="space-y-8">
                        <div>
                            <Label htmlFor="name" className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-white/50">Your name *</Label>
                            <input id="name" required value={form.name} onChange={update("name")} placeholder="Ramesh Gupta" className="industrial-input" data-testid="enquiry-name-input" />
                        </div>
                        <div className="grid gap-8 md:grid-cols-2">
                            <div>
                                <Label htmlFor="phone" className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-white/50">Phone *</Label>
                                <input id="phone" required type="tel" value={form.phone} onChange={update("phone")} placeholder="+91 ..." className="industrial-input" data-testid="enquiry-phone-input" />
                            </div>
                            <div>
                                <Label htmlFor="email" className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-white/50">Email</Label>
                                <input id="email" type="email" value={form.email} onChange={update("email")} placeholder="you@company.com" className="industrial-input" data-testid="enquiry-email-input" />
                            </div>
                        </div>
                        <div>
                            <Label className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-white/50">What are you moving? *</Label>
                            <div className="flex flex-wrap gap-2" data-testid="enquiry-service-options">
                                {SERVICES.map((service) => (
                                    <button
                                        key={service}
                                        type="button"
                                        onClick={() => setForm({ ...form, service })}
                                        data-testid={`enquiry-service-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className={`border px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors duration-200 ${
                                            form.service === service
                                                ? "border-blaze bg-blaze text-white"
                                                : "border-white/20 text-white/60 hover:border-blaze hover:text-blaze"
                                        }`}
                                    >
                                        {service}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <Label htmlFor="message" className="mb-3 block text-[11px] uppercase tracking-[0.25em] text-white/50">Route &amp; details</Label>
                            <textarea
                                id="message"
                                rows={4}
                                value={form.message}
                                onChange={update("message")}
                                placeholder="e.g. 2 x 40 ft containers, Mundra Port to Bhiwandi, first week of August"
                                className="industrial-input resize-none"
                                data-testid="enquiry-message-input"
                            />
                        </div>
                        <button
                            type="submit"
                            disabled={loading}
                            data-testid="enquiry-submit-button"
                            className="group flex w-full items-center justify-center gap-3 bg-blaze px-8 py-5 text-xs font-semibold uppercase tracking-[0.25em] text-white transition-colors duration-200 hover:bg-white hover:text-void disabled:opacity-60"
                        >
                            {loading ? (
                                <>
                                    <CircleNotch size={18} weight="bold" className="animate-spin" />
                                    Sending...
                                </>
                            ) : (
                                <>
                                    Send Enquiry
                                    <ArrowUpRight size={16} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                                </>
                            )}
                        </button>
                        <p className="text-center text-[11px] uppercase tracking-[0.2em] text-white/30">
                            Response within one working hour, 9 AM – 9 PM IST
                        </p>
                    </div>
                </motion.form>
            </div>
        </section>
    );
}
